import { API_BASE_URL } from '@types';
import type {
  BodyImageComparison,
  BodyImageComparisonMetric,
  BodyImageSessionNote,
  BodyImageSessionSummary,
  BodyImageTrackingEvent,
  CreateBodyImageComparisonPayload,
  CreateBodyImageSessionNotePayload,
  CreateBodyImageTrackingEventPayload,
  UpdateBodyImageSessionNotePayload,
  UpdateBodyImageComparisonPayload,
} from '@types';

const requestJson = async <T>(input: RequestInfo, init?: RequestInit) => {
  const requestUrl = typeof input === 'string' ? input : input.toString();
  const requestMethod = init?.method ?? 'GET';

  const response = await fetch(input, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const responseText = await response.text();
    const error = new Error(
      `HTTP ${response.status}: ${response.statusText}${responseText ? `\n${responseText}` : ''}`
    );
    (error as Error & { status?: number }).status = response.status;
    console.error('[bodyImageSessionManager] request failed', {
      url: requestUrl,
      method: requestMethod,
      status: response.status,
      statusText: response.statusText,
      requestBody:
        typeof init?.body === 'string'
          ? init.body
          : init?.body != null
            ? '[non-string body]'
            : null,
      responseText,
    });
    throw error;
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
};

const unwrapData = <T>(payload: unknown): T => {
  if (
    payload != null &&
    typeof payload === 'object' &&
    'data' in (payload as Record<string, unknown>)
  ) {
    return (payload as { data: T }).data;
  }

  return payload as T;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  return null;
};

const toObject = (value: unknown): Record<string, unknown> => {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
};

const normalizeComparisonMetric = (value: unknown): BodyImageComparisonMetric | null => {
  const record = toObject(value);
  const id = toNumber(record.id);
  const comparisonId = toNumber(record.comparison_id ?? record.comparisonId);
  const metricKey = toStringOrNull(record.metric_key ?? record.metricKey);

  if (id == null || comparisonId == null || metricKey == null) {
    return null;
  }

  return {
    id,
    comparisonId,
    metricKey,
    patientValue: toNumber(record.patient_value ?? record.patientValue),
    comparisonValue: toNumber(record.comparison_value ?? record.comparisonValue),
    differencePercent: toNumber(
      record.difference_percent ?? record.differencePercent
    ),
    createdAt: toStringOrNull(record.created_at ?? record.createdAt),
  };
};

const normalizeComparison = (value: unknown): BodyImageComparison | null => {
  const record = toObject(value);
  const id = toNumber(record.id);
  const sessionId = toNumber(record.session_id ?? record.sessionId);
  const patientId = toNumber(record.patient_id ?? record.patientId);

  if (id == null || sessionId == null || patientId == null) {
    return null;
  }

  const metricsRaw = record.metrics;
  const metrics = Array.isArray(metricsRaw)
    ? metricsRaw
        .map(item => normalizeComparisonMetric(item))
        .filter((item): item is BodyImageComparisonMetric => item != null)
    : [];

  const status = toStringOrNull(record.status) ?? 'pending';

  return {
    id,
    sessionId,
    patientId,
    status: status as BodyImageComparison['status'],
    label: toStringOrNull(record.label),
    notes: toStringOrNull(record.notes),
    parameters: toObject(record.parameters_json ?? record.parametersJson ?? record.parameters),
    baselineMeasurements: Object.fromEntries(
      Object.entries(
        toObject(
          record.baseline_measurements_json ??
            record.baselineMeasurementsJson ??
            record.baselineMeasurements
        )
      )
        .map(([key, item]) => [key, toNumber(item)])
        .filter((entry): entry is [string, number] => entry[1] != null)
    ),
    metrics,
    createdAt: toStringOrNull(record.created_at ?? record.createdAt),
    updatedAt: toStringOrNull(record.updated_at ?? record.updatedAt),
  };
};

const normalizeTrackingEvent = (value: unknown): BodyImageTrackingEvent | null => {
  const record = toObject(value);
  const id = toNumber(record.id);
  const sessionId = toNumber(record.session_id ?? record.sessionId);
  const patientId = toNumber(record.patient_id ?? record.patientId);
  const eventName = toStringOrNull(record.event_name ?? record.eventName);

  if (id == null || sessionId == null || patientId == null || eventName == null) {
    return null;
  }

  return {
    id,
    sessionId,
    patientId,
    eventName,
    direction:
      (toStringOrNull(record.direction) as BodyImageTrackingEvent['direction']) ??
      'outbound',
    eventPayload: toObject(
      record.event_payload_json ?? record.eventPayloadJson ?? record.eventPayload
    ),
    createdAt: toStringOrNull(record.created_at ?? record.createdAt),
  };
};

const normalizeNote = (value: unknown): BodyImageSessionNote | null => {
  const record = toObject(value);
  const id = toNumber(record.id);
  const sessionId = toNumber(record.session_id ?? record.sessionId);
  const patientId = toNumber(record.patient_id ?? record.patientId);
  const title = toStringOrNull(record.title);
  const description = toStringOrNull(record.description);

  if (
    id == null ||
    sessionId == null ||
    patientId == null ||
    title == null ||
    description == null
  ) {
    return null;
  }

  return {
    id,
    sessionId,
    patientId,
    title,
    description,
    createdAt: toStringOrNull(record.created_at ?? record.createdAt),
    updatedAt: toStringOrNull(record.updated_at ?? record.updatedAt),
  };
};

export async function fetchBodyImageTrackingEvents(
  sessionId: number | string
): Promise<BodyImageTrackingEvent[]> {
  try {
    const payload = await requestJson<unknown>(
      `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/tracking-events`
    );
    const data = unwrapData<unknown>(payload);
    const items = Array.isArray(data)
      ? data
      : Array.isArray((data as Record<string, unknown> | null)?.items)
        ? (((data as Record<string, unknown>).items as unknown[]) ?? [])
        : [];

    return items
      .map(item => normalizeTrackingEvent(item))
      .filter((item): item is BodyImageTrackingEvent => item != null);
  } catch (error) {
    if ((error as Error & { status?: number }).status === 404) {
      return [];
    }
    throw error;
  }
}

export async function createBodyImageTrackingEvent(
  sessionId: number | string,
  values: CreateBodyImageTrackingEventPayload
): Promise<BodyImageTrackingEvent> {
  const requestBody = {
    patient_id: values.patientId,
    event_name: values.eventName,
    direction: values.direction ?? 'outbound',
    event_payload_json: values.eventPayload,
  };

  console.info('[bodyImageSessionManager] create tracking event request', {
    sessionId,
    body: requestBody,
  });

  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/tracking-events`,
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }
  );

  console.info('[bodyImageSessionManager] create tracking event response', {
    sessionId,
    payload,
  });

  const normalized = normalizeTrackingEvent(unwrapData<unknown>(payload));
  if (normalized == null) {
    throw new Error('Invalid body image tracking event payload');
  }
  return normalized;
}

export async function deleteBodyImageTrackingEvent(
  sessionId: number | string,
  trackingEventId: number
): Promise<void> {
  console.info('[bodyImageSessionManager] delete tracking event request', {
    sessionId,
    trackingEventId,
  });
  await requestJson(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/tracking-events/${trackingEventId}`,
    { method: 'DELETE' }
  );
}

export async function deleteAllBodyImageTrackingEvents(
  sessionId: number | string
): Promise<void> {
  const collectionUrl = `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/tracking-events`;

  try {
    console.info('[bodyImageSessionManager] delete all tracking events request', {
      sessionId,
      url: collectionUrl,
    });
    await requestJson(collectionUrl, { method: 'DELETE' });
    return;
  } catch (error) {
    const status = (error as Error & { status?: number }).status;
    console.warn('[bodyImageSessionManager] delete all tracking events failed', {
      sessionId,
      status,
      message: error instanceof Error ? error.message : String(error),
    });
    if (status === 404) {
      return;
    }
  }

  const events = await fetchBodyImageTrackingEvents(sessionId).catch(() => []);
  console.info('[bodyImageSessionManager] delete all tracking events fallback list', {
    sessionId,
    count: events.length,
  });
  if (events.length === 0) {
    return;
  }

  await Promise.allSettled(
    events.map(event => deleteBodyImageTrackingEvent(sessionId, event.id))
  );
}

export async function fetchBodyImageComparisons(
  sessionId: number | string
): Promise<BodyImageComparison[]> {
  try {
    const payload = await requestJson<unknown>(
      `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/comparisons`
    );
    const data = unwrapData<unknown>(payload);
    const items = Array.isArray(data)
      ? data
      : Array.isArray((data as Record<string, unknown> | null)?.items)
        ? (((data as Record<string, unknown>).items as unknown[]) ?? [])
        : [];

    return items
      .map(item => normalizeComparison(item))
      .filter((item): item is BodyImageComparison => item != null);
  } catch (error) {
    if ((error as Error & { status?: number }).status === 404) {
      return [];
    }
    throw error;
  }
}

export async function createBodyImageComparison(
  sessionId: number | string,
  values: CreateBodyImageComparisonPayload
): Promise<BodyImageComparison> {
  const requestBody = {
    patient_id: values.patientId,
    label: values.label ?? null,
    notes: values.notes ?? null,
    parameters_json: values.parameters,
    baseline_measurements_json: values.baselineMeasurements,
    metrics: values.metrics,
  };

  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/comparisons`,
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }
  );

  const normalized = normalizeComparison(unwrapData<unknown>(payload));
  if (normalized == null) {
    throw new Error('Invalid body image comparison payload');
  }
  return normalized;
}

export async function updateBodyImageComparison(
  sessionId: number | string,
  comparisonId: number,
  values: UpdateBodyImageComparisonPayload
): Promise<BodyImageComparison> {
  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/comparisons/${comparisonId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(values),
    }
  );

  const normalized = normalizeComparison(unwrapData<unknown>(payload));
  if (normalized == null) {
    throw new Error('Invalid body image comparison update payload');
  }
  return normalized;
}

export async function deleteBodyImageComparison(
  sessionId: number | string,
  comparisonId: number
): Promise<void> {
  await requestJson<unknown>(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/comparisons/${comparisonId}`,
    {
      method: 'DELETE',
    }
  );
}

export async function fetchBodyImageNotes(
  sessionId: number | string
): Promise<BodyImageSessionNote[]> {
  try {
    const payload = await requestJson<unknown>(
      `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/notes`
    );
    const data = unwrapData<unknown>(payload);
    const items = Array.isArray(data)
      ? data
      : Array.isArray((data as Record<string, unknown> | null)?.items)
        ? (((data as Record<string, unknown>).items as unknown[]) ?? [])
        : [];

    return items
      .map(item => normalizeNote(item))
      .filter((item): item is BodyImageSessionNote => item != null);
  } catch (error) {
    if ((error as Error & { status?: number }).status === 404) {
      return [];
    }
    throw error;
  }
}

export async function createBodyImageNote(
  sessionId: number | string,
  values: CreateBodyImageSessionNotePayload
): Promise<BodyImageSessionNote> {
  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/notes`,
    {
      method: 'POST',
      body: JSON.stringify({
        patient_id: values.patientId,
        title: values.title,
        description: values.description,
      }),
    }
  );

  const normalized = normalizeNote(unwrapData<unknown>(payload));
  if (normalized == null) {
    throw new Error('Invalid body image note payload');
  }
  return normalized;
}

export async function updateBodyImageNote(
  sessionId: number | string,
  noteId: number,
  values: UpdateBodyImageSessionNotePayload
): Promise<BodyImageSessionNote> {
  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/notes/${noteId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        title: values.title,
        description: values.description,
      }),
    }
  );

  const normalized = normalizeNote(unwrapData<unknown>(payload));
  if (normalized == null) {
    throw new Error('Invalid body image note update payload');
  }
  return normalized;
}

export async function deleteBodyImageNote(
  sessionId: number | string,
  noteId: number
): Promise<void> {
  await requestJson(
    `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/notes/${noteId}`,
    { method: 'DELETE' }
  );
}

export async function fetchBodyImageSessionSummary(
  sessionId: number | string
): Promise<BodyImageSessionSummary> {
  try {
    const payload = await requestJson<unknown>(
      `${API_BASE_URL}/services/v1/configurations/${sessionId}/body-image/summary`
    );
    const data = toObject(unwrapData<unknown>(payload));

    const trackingEvents = Array.isArray(data.trackingEvents)
      ? data.trackingEvents
          .map(item => normalizeTrackingEvent(item))
          .filter((item): item is BodyImageTrackingEvent => item != null)
      : [];

    const comparisons = Array.isArray(data.comparisons)
      ? data.comparisons
          .map(item => normalizeComparison(item))
          .filter((item): item is BodyImageComparison => item != null)
      : [];

    const notes = Array.isArray(data.notes)
      ? data.notes
          .map(item => normalizeNote(item))
          .filter((item): item is BodyImageSessionNote => item != null)
      : [];

    return {
      trackingEvents,
      comparisons,
      notes,
    };
  } catch (error) {
    if ((error as Error & { status?: number }).status === 404) {
      return {
        trackingEvents: [],
        comparisons: [],
        notes: [],
      };
    }
    throw error;
  }
}
